import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

import { MetricMealDetail, ChatAdjustmentMealContent } from '@demind-inc/core';

import { ChatSuggestedEvent, UnifiedChatRichContext } from '../types';
import { MealAdjustment, SleepSchedule } from '../api/science/openapi';

// Kept dependency-light on purpose (no component barrel imports) — mirrors
// resultJsonSchedule.helpers.ts's own stated reasoning: this is called from
// usePlanEntryHandler.ts, which must stay safe to unit test in isolation.
export const TEMPORAL_MEAL_SLEEP_PREFIX = 'temporal-';

/** Same shape reader as resultJsonSchedule.helpers.ts's private
 * extractMealContentFromRichContext, duplicated locally for the same reason
 * (avoid pulling in that file's deps). */
const extractMealContentFromRichContext = (
  richContext: unknown
): ChatAdjustmentMealContent | undefined => {
  const rc = richContext as { content?: unknown } | null | undefined;
  const content = Array.isArray(rc?.content) ? rc?.content[0] : rc?.content;
  const mealPayload =
    (content as { type?: string; payload?: Record<string, unknown> } | undefined)?.type ===
    'meal_suggestion'
      ? ((content as { payload: Record<string, unknown> }).payload as Record<string, any>)
      : null;
  if (!mealPayload) {
    return undefined;
  }
  return {
    title: mealPayload.title,
    foodItems: mealPayload.food_items,
    macros: mealPayload.macros,
    micros: mealPayload.micros,
  };
};

/** MealAdjustment.content carries the same MealSuggestionPayload shape as
 * rich_context.content.payload (snake_case food_items etc.), just without the
 * {type, payload} wrapper — used as a fallback when rich_context hasn't
 * arrived yet (it can lag behind via enrichment_patch, same as schedule). */
const extractMealContentFromPayload = (
  payload: MealAdjustment['content']
): ChatAdjustmentMealContent | undefined => {
  if (!payload) {
    return undefined;
  }
  const raw = payload as Record<string, any>;
  return {
    title: raw.title,
    foodItems: raw.food_items,
    macros: raw.macros,
    micros: raw.micros,
  };
};

/** Same clock check as lifestack_backend's transformAdjustmentCTA — drops
 * missing times and the string "null" the API sends for a cleared meal. */
const isValidHHmm = (value: unknown): value is string =>
  typeof value === 'string' &&
  value.toLowerCase() !== 'null' &&
  /^\d{1,2}:\d{2}$/.test(value.trim());

/** Meals stay on `date`. Mirrors backend transformHHmmToDate. */
const transformHHmmToTargetDateISO = (time: string, date: string): string => {
  const [hour, minute] = time.split(':').map((value) => parseInt(value, 10));
  const dateStr = `${date}T${hour.toString().padStart(2, '0')}:${minute
    .toString()
    .padStart(2, '0')}:00`;
  return dayjs.tz(dateStr, dayjs.tz.guess()).toISOString();
};

/** Sleep/wake can cross midnight. Hours 00:00–15:59 land on the next day,
 * matching backend transformHHmmToDateConsideringMidnight. */
const transformHHmmToDateConsideringMidnightISO = (time: string, date: string): string => {
  const [hour, minute] = time.split(':').map((value) => parseInt(value, 10));
  const isAfterMidnight = hour >= 0 && hour < 16;
  const targetDate = isAfterMidnight ? dayjs(date).add(1, 'day').format('YYYY-MM-DD') : date;
  const dateStr = `${targetDate}T${hour.toString().padStart(2, '0')}:${minute
    .toString()
    .padStart(2, '0')}:00`;
  return dayjs.tz(dateStr, dayjs.tz.guess()).toISOString();
};

/**
 * Maps a v4 result_json's raw `adjustments.sleep` (a day singleton, no id)
 * into a ChatSuggestedEvent, or undefined if absent/incomplete. eventId is
 * intentionally NOT time-based — a reschedule changes ideal_sleep_time/
 * ideal_wake_time by definition, so baking them into the id would produce a
 * different id on every update turn.
 */
export const transformResultJsonSleepToSuggestedEvent = (
  sleep: SleepSchedule | undefined,
  date: string
): ChatSuggestedEvent | undefined => {
  if (!sleep?.ideal_sleep_time || !sleep?.ideal_wake_time) {
    return undefined;
  }

  return {
    eventId: `${TEMPORAL_MEAL_SLEEP_PREFIX}sleep-${date}`,
    // Fresh per call (unlike eventId) so a sleep card from an earlier turn
    // gets treated as a new, not-yet-accepted suggestion.
    blockInstanceId: `${TEMPORAL_MEAL_SLEEP_PREFIX}sleep-${date}-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 8)}`,
    name: '😴 Sleep',
    startTime: transformHHmmToDateConsideringMidnightISO(sleep.ideal_sleep_time, date),
    endTime: transformHHmmToDateConsideringMidnightISO(sleep.ideal_wake_time, date),
    description: sleep.explanation ?? undefined,
    mealContent: undefined,
    richContext: sleep.rich_context as UnifiedChatRichContext | undefined,
    autoScheduleFrom: {
      from: 'sleep',
      id: `${TEMPORAL_MEAL_SLEEP_PREFIX}sleep-${date}`,
      predicted: true,
    },
    isNew: true,
    action: 'setEvent',
  };
};

/**
 * Maps a v4 result_json's raw `adjustments.meals` (MealAdjustment[]) into
 * ChatSuggestedEvent[], skipping `action: 'remove'` items (the caller evicts
 * those from earlier chat cards by eventId). eventId keys off meal name +
 * date, not start/end time.
 */
export const transformResultJsonMealsToSuggestedEvents = (
  meals: MealAdjustment[],
  date: string,
  // Already-logged meals for this date (MetricsProvider's getMealOnDate),
  // keyed by MetricMealDetail.type ('breakfast'/'lunch'/'dinner'). Backend
  // `action` is documented/typed as 'add'|'update'|'remove' but has been
  // observed sent as null even for a meal that's already logged.
  existingMealDetails: MetricMealDetail[] = []
): ChatSuggestedEvent[] => {
  const instanceSeed = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  return meals
    .filter(
      (meal) =>
        meal.action !== 'remove' &&
        meal.name &&
        isValidHHmm(meal.start_time) &&
        isValidHHmm(meal.end_time)
    )
    .map((meal, index): ChatSuggestedEvent => {
      const mealType = meal.name.toLowerCase();
      const eventId = `${TEMPORAL_MEAL_SLEEP_PREFIX}meal-${mealType}-${date}`;
      const startTime = transformHHmmToTargetDateISO(meal.start_time, date);
      const endTime = transformHHmmToTargetDateISO(meal.end_time, date);
      const isUpdate =
        meal.action === 'update' || existingMealDetails.some((detail) => detail.type === mealType);
      return {
        eventId,
        blockInstanceId: `${eventId}-${instanceSeed}-${index}`,
        name: `🍽️ ${meal.name}`,
        startTime,
        endTime,
        description: meal.explanation ?? undefined,
        mealContent:
          extractMealContentFromRichContext(meal.rich_context) ??
          extractMealContentFromPayload(meal.content),
        richContext: meal.rich_context as UnifiedChatRichContext | undefined,
        autoScheduleFrom: { from: 'meal', id: eventId, predicted: true },
        before: isUpdate ? { startTime, endTime } : undefined,
        isNew: !isUpdate,
        action: 'setEvent',
      };
    });
};

/** eventIds of meals this turn marked `action: 'remove'`. Meal/sleep blocks
 * never enter temporalSuggestedEvents (calendar grid overlay). */
export const getRemovedMealEventIds = (meals: MealAdjustment[], date: string): string[] =>
  meals
    .filter((meal) => meal.action === 'remove' && meal.name)
    .map((meal) => `${TEMPORAL_MEAL_SLEEP_PREFIX}meal-${meal.name.toLowerCase()}-${date}`);
